import type { Component, VNode } from "vue"
import type { ToastProps } from "."
import { computed, ref } from "vue"
import { App as AntApp, notification } from 'ant-design-vue'
import type { NotificationInstance } from 'ant-design-vue/es/notification/interface'

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 1000000

export type StringOrVNode
  = | string
    | VNode
    | (() => VNode)

type ToasterToast = ToastProps & {
  id: string
  title?: string
  description?: StringOrVNode
  action?: Component
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

type ActionType = typeof actionTypes

type Action
  = | {
    type: ActionType["ADD_TOAST"]
    toast: ToasterToast
  }
  | {
    type: ActionType["UPDATE_TOAST"]
    toast: Partial<ToasterToast>
  }
  | {
    type: ActionType["DISMISS_TOAST"]
    toastId?: ToasterToast["id"]
  }
  | {
    type: ActionType["REMOVE_TOAST"]
    toastId?: ToasterToast["id"]
  }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

function addToRemoveQueue(toastId: string) {
  if (toastTimeouts.has(toastId))
    return

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

const state = ref<State>({
  toasts: [],
})

function dispatch(action: Action) {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      ;(state.value.toasts as any).unshift(action.toast)
      if (state.value.toasts.length > TOAST_LIMIT) {
        state.value.toasts = state.value.toasts.slice(0, TOAST_LIMIT)
      }
      break

    case actionTypes.UPDATE_TOAST:
      state.value.toasts = (state.value.toasts as any).map((t: any) =>
        t.id === action.toast.id ? ({ ...t, ...action.toast } as any) : t,
      )
      break

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      }
      else {
        state.value.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      state.value.toasts = (state.value.toasts as any).map((t: any) =>
        t.id === toastId || toastId === undefined
          ? ({
              ...t,
              open: false,
            } as any)
          : t,
      )
      break
    }

    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined)
        state.value.toasts = []
      else
        state.value.toasts = (state.value.toasts as any).filter((t: any) => t.id !== action.toastId)

      break
  }
}

function useToast() {
  const antNotification = AntApp.useApp().notification
  return {
    toasts: computed(() => state.value.toasts),
    toast: (props: Toast) => toast(props, antNotification),
    dismiss: (toastId?: string) => dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  }
}

type Toast = Omit<ToasterToast, "id">

function toast(props: Toast, antNotification: NotificationInstance = notification as NotificationInstance) {
  const id = genId()

  if (typeof document !== 'undefined' && document.querySelector('.crm-shell')) {
    const description = typeof props.description === 'string' ? props.description : undefined
    antNotification.open({
      key: id,
      type: props.variant === 'destructive' ? 'error' : 'success',
      message: props.title || '',
      description,
      placement: 'topRight',
    })

    return {
      id,
      dismiss: () => antNotification.destroy(id),
      update: (next: ToasterToast) => antNotification.open({
        key: id,
        type: next.variant === 'destructive' ? 'error' : 'success',
        message: next.title || '',
        description: typeof next.description === 'string' ? next.description : undefined,
      }),
    }
  }

  const update = (props: ToasterToast) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...props, id },
    })

  const dismiss = () => dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id })

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open)
          dismiss()
      },
    },
  })

  return {
    id,
    dismiss,
    update,
  }
}

export { toast, useToast }
